const normalizeVariantValue = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s*&\s*/g, '&');

const pickPreferredVariant = (variants = []) => {
  if (!variants.length) return null;
  return variants.find((variant) => variant.available) || variants[0];
};

const resolveConfiguredVariant = (product, configuredValue) => {
  const normalizedValue = normalizeVariantValue(configuredValue);
  if (!normalizedValue) return { status: 'missing', variant: null };
  const variants = product?.variants || [];
  const titleMatches = variants.filter((variant) => normalizeVariantValue(variant.title) === normalizedValue);
  const optionMatches = variants.filter((variant) =>
    (variant.options || []).some((option) => normalizeVariantValue(option) === normalizedValue)
  );
  const matches = titleMatches.length ? titleMatches : optionMatches;
  if (!matches.length) return { status: 'missing', variant: null };
  if (matches.length === 1) return { status: 'matched', variant: matches[0] };
  return { status: 'ambiguous', variant: null };
};

const resolveLineVariant = (product, configuredValue) => {
  if (!product?.variants?.length) return { status: 'missing', variant: null };
  const normalizedValue = normalizeVariantValue(configuredValue);
  if (!normalizedValue) {
    const picked = pickPreferredVariant(product.variants);
    return picked ? { status: 'matched', variant: picked } : { status: 'missing', variant: null };
  }
  return resolveConfiguredVariant(product, configuredValue);
};

const displayChairCount = (count) => (Number(count) > 0 ? Number(count) : 1);

const pairPrices = (tableVariant, chairVariant, chairCount, percentage) => {
  const tablePrice = Number(tableVariant?.price);
  const chairPrice = Number(chairVariant?.price);
  const count = displayChairCount(chairCount);
  const discount = Number(percentage);
  if (
    !Number.isFinite(tablePrice)
    || !Number.isFinite(chairPrice)
    || !Number.isFinite(discount)
    || discount <= 0
    || discount > 100
  ) {
    return null;
  }

  const original = tablePrice + chairPrice * count;
  const tableCompareValue = Number(tableVariant.compare_at_price);
  const chairCompareValue = Number(chairVariant.compare_at_price);
  const tableCompare = Number.isFinite(tableCompareValue) && tableCompareValue > tablePrice
    ? tableCompareValue
    : tablePrice;
  const chairCompare = Number.isFinite(chairCompareValue) && chairCompareValue > chairPrice
    ? chairCompareValue
    : chairPrice;
  const compareAt = tableCompare + chairCompare * count;
  const showCompare = compareAt > original;

  return {
    original,
    compareAt: showCompare ? compareAt : null,
    promotional: Math.round(original * (100 - discount) / 100),
    showCompare,
  };
};

const variantDetailsUrl = (product, variant) => {
  if (!product?.url || !variant?.id) return '';
  return `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${variant.id}`;
};

const displayImage = (product, configuredValue) =>
  resolveLineVariant(product, configuredValue).variant?.featured_image || product?.featured_image || null;

const canFulfill = (variant, quantity) => {
  if (!variant?.available || !Number.isInteger(quantity) || quantity <= 0) return false;
  if (!variant.inventory_management || variant.inventory_policy === 'continue') return true;
  return Number(variant.inventory_quantity) >= quantity;
};

const mergeDiscountCodes = (existing, configured) => {
  const codes = [];
  const seen = new Set();
  for (const value of [...(existing || []), configured]) {
    const code = String(value || '').trim();
    const normalized = code.toLowerCase();
    if (!code || seen.has(normalized)) continue;
    seen.add(normalized);
    codes.push(code);
  }
  return codes;
};

const discountIsApplicable = (cart, code) => {
  const normalizedCode = String(code || '').trim().toLowerCase();
  return Boolean(normalizedCode && (cart?.discount_codes || []).some((discount) =>
    String(discount.code || '').trim().toLowerCase() === normalizedCode && discount.applicable
  ));
};

const uniqueBy = (items, keyFn) => {
  const seen = new Set();
  const result = [];
  for (const item of items) {
    const key = keyFn(item);
    if (key == null || seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
};

const cyclicIndex = (index, direction, total) => {
  if (!total) return 0;
  return ((index + direction) % total + total) % total;
};

const loopTrackIndex = (fromIndex, toIndex, direction, total) => {
  if (total < 2) return toIndex;
  if (fromIndex === total - 1 && toIndex === 0 && direction > 0) return total + 1;
  if (fromIndex === 0 && toIndex === total - 1 && direction < 0) return 0;
  return toIndex + 1;
};

window.ProductPairingUtils = {
  normalizeVariantValue, pickPreferredVariant, resolveConfiguredVariant, resolveLineVariant, canFulfill,
  pairPrices, variantDetailsUrl, displayImage,
  mergeDiscountCodes, discountIsApplicable, uniqueBy,
  cyclicIndex, loopTrackIndex, displayChairCount,
};

if (!customElements.get('product-pairing')) {
  customElements.define(
    'product-pairing',
    class ProductPairing extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handlePointerCancel = this.handlePointerCancel.bind(this);
        this.dragStarts = new Map();
        this.sliderTransitions = new Map();
        this.suppressProductLinkClick = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        try {
          const data = JSON.parse(this.querySelector('[data-pairing-products]')?.textContent || '{}');
          this.entries = Array.isArray(data.entries) ? data.entries : [];
          this.discount = data.discount || {};
          this.couponSelected = Boolean(this.querySelector('[data-pairing-coupon]')?.checked);
          this.baseValid = this.dataset.pairingValid === 'true';
          this.tables = uniqueBy(this.entries, (entry) => entry.tableId);
          const first = this.entries[0];
          this.state = {
            tableId: first?.tableId ?? null,
            chairId: first?.chairId ?? null,
            chairCount: first?.chairCount ?? 0,
          };
          this.ensureValidState();
          this.cartDrawer = document.querySelector('cart-drawer');
          const configuration = this.querySelector('[data-pairing-configuration]');
          if (configuration) configuration.hidden = true;
        } catch (error) {
          this.baseValid = false;
          this.configurationValid = false;
          this.entries = [];
          this.tables = [];
          this.tableProduct = null;
          this.tableVariant = null;
          this.chairProduct = null;
          this.chairVariant = null;
          this.prices = null;
          this.state = { tableId: null, chairId: null, chairCount: 0 };
          this.showError(this.dataset.cartError);
          const configuration = this.querySelector('[data-pairing-configuration]');
          if (configuration) configuration.hidden = true;
        }

        this.rebuildChairSlider({ resetPosition: true });
        this.rebuildSetButtons();
        this.initializeSliderLoops();
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('pointerdown', this.handlePointerDown);
        this.addEventListener('pointerup', this.handlePointerUp);
        this.addEventListener('pointercancel', this.handlePointerCancel);
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('pointerdown', this.handlePointerDown);
        this.removeEventListener('pointerup', this.handlePointerUp);
        this.removeEventListener('pointercancel', this.handlePointerCancel);
        this.sliderTransitions.forEach((transition, type) => this.cancelSliderTransition(type, transition));
        for (const type of ['table', 'chair']) {
          const slider = this.querySelector(`[data-product-slider="${type}"]`);
          const track = slider?.querySelector('[data-product-track]');
          if (track) this.normalizeSlider(type, track, false);
        }
        this.initialized = false;
      }

      chairsForTable(tableId) {
        return uniqueBy(
          this.entries.filter((entry) => entry.tableId === tableId),
          (entry) => entry.chairId,
        );
      }

      setsForPair(tableId, chairId) {
        const sets = this.entries
          .filter((entry) => entry.tableId === tableId && entry.chairId === chairId)
          .map((entry) => ({
            chairCount: Number(entry.chairCount) || 0,
            setLabel: entry.setLabel,
            entryId: entry.id,
          }));
        return uniqueBy(sets, (set) => set.chairCount);
      }

      currentEntry() {
        return this.entries.find((entry) => (
          entry.tableId === this.state.tableId
          && entry.chairId === this.state.chairId
          && Number(entry.chairCount) === Number(this.state.chairCount)
        )) || null;
      }

      ensureValidState() {
        if (!this.tables.length) return;
        if (!this.tables.some((entry) => entry.tableId === this.state.tableId)) {
          this.state.tableId = this.tables[0].tableId;
        }
        const chairs = this.chairsForTable(this.state.tableId);
        if (!chairs.some((entry) => entry.chairId === this.state.chairId)) {
          this.state.chairId = chairs[0]?.chairId ?? null;
        }
        const sets = this.setsForPair(this.state.tableId, this.state.chairId);
        if (!sets.some((set) => set.chairCount === Number(this.state.chairCount))) {
          const available = sets.find((set) => this.setAvailable(set.chairCount)) || sets[0];
          this.state.chairCount = available?.chairCount ?? 0;
        } else if (!this.setAvailable(this.state.chairCount)) {
          const fallback = sets.find((set) => this.setAvailable(set.chairCount));
          if (fallback) this.state.chairCount = fallback.chairCount;
        }
      }

      setLabelFor(count, customLabel) {
        if (customLabel) return customLabel;
        if (Number(count) === 0) return this.dataset.setStandardLabel || 'Standard';
        // Set options represent chair quantity (e.g. 2 / 3 / 4 / 6 chairs).
        return (this.dataset.setOfCountTemplate || this.dataset.chairQuantityTemplate || '__COUNT__ × Chairs')
          .replace('__COUNT__', String(count));
      }

      createLoopClone(slide) {
        const clone = slide.cloneNode(true);
        clone.classList.add('product-pairing__slide--clone');
        clone.classList.remove('is-active');
        clone.removeAttribute('data-product-slide');
        clone.removeAttribute('data-index');
        clone.removeAttribute('data-product-id');
        clone.removeAttribute('id');
        clone.setAttribute('tabindex', '-1');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        if ('inert' in clone) clone.inert = true;
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        return clone;
      }

      sliderTotal(type) {
        if (type === 'table') return this.tables.length;
        if (type === 'chair') return this.chairsForTable(this.state.tableId).length;
        return 0;
      }

      currentIndex(type) {
        if (type === 'table') {
          return Math.max(0, this.tables.findIndex((entry) => entry.tableId === this.state.tableId));
        }
        if (type === 'chair') {
          return Math.max(0, this.chairsForTable(this.state.tableId).findIndex((entry) => entry.chairId === this.state.chairId));
        }
        if (type === 'set') {
          return Math.max(0, this.setsForPair(this.state.tableId, this.state.chairId)
            .findIndex((set) => set.chairCount === Number(this.state.chairCount)));
        }
        return 0;
      }

      rebuildChairSlider({ resetPosition = false } = {}) {
        const track = this.querySelector('[data-chair-track]');
        const dots = this.querySelector('[data-pairing-dots="chair"]');
        const slider = this.querySelector('[data-product-slider="chair"]');
        if (!track || !dots) return;

        const chairs = this.chairsForTable(this.state.tableId);
        const activeIndex = Math.max(0, chairs.findIndex((entry) => entry.chairId === this.state.chairId));
        const count = displayChairCount(this.state.chairCount);
        const quantity = (this.dataset.chairQuantityTemplate || '__COUNT__').replace('__COUNT__', String(count));

        track.innerHTML = chairs.map((entry, index) => {
          const image = displayImage(entry.chair, entry.chairVariant);
          const lineVariant = resolveLineVariant(entry.chair, entry.chairVariant).variant;
          const productUrl = variantDetailsUrl(entry.chair, lineVariant) || entry.chair?.url || '#';
          const title = entry.chair?.title || entry.chairAlias || '';
          const imageHtml = image?.src
            ? `<img src="${image.src}" alt="${this.escapeAttr(title)}" loading="lazy" width="600" height="600">`
            : '<svg class="placeholder-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" fill="#f2f2f2"/></svg>';
          return `
            <article
              class="product-pairing__slide${index === activeIndex ? ' is-active' : ''}"
              data-product-slide
              data-product-id="${entry.chairId}"
              aria-hidden="${index === activeIndex ? 'false' : 'true'}"
              data-index="${index}"
            >
              <a
                class="product-pairing__product-link"
                href="${this.escapeAttr(productUrl)}"
                data-product-link
                data-product-type="chair"
                aria-label="${this.escapeAttr(title)}"
              >
                <div class="product-pairing__product-image">${imageHtml}</div>
              </a>
            </article>
          `;
        }).join('');

        dots.innerHTML = chairs.map((_, index) => `
          <button
            type="button"
            class="product-pairing__dot${index === activeIndex ? ' is-active' : ''}"
            data-pairing-dot
            data-product-type="chair"
            data-index="${index}"
            aria-pressed="${index === activeIndex ? 'true' : 'false'}"
            aria-label="${index + 1} / ${chairs.length}"
          ><span></span></button>
        `).join('');

        if (slider) {
          slider.dataset.loopReady = 'false';
          const arrows = slider.querySelectorAll('[data-pairing-arrow]');
          arrows.forEach((arrow) => {
            arrow.hidden = chairs.length < 2;
          });
          dots.hidden = chairs.length < 2;
        }

        if (resetPosition) {
          track.classList.add('is-resetting');
          track.style.transform = chairs.length > 1 ? 'translateX(-100%)' : 'translateX(0)';
          track.getBoundingClientRect();
          track.classList.remove('is-resetting');
        }
      }

      rebuildSetButtons() {
        const container = this.querySelector('[data-set-buttons]');
        const area = this.querySelector('[data-set-area]');
        if (!container) return;

        const sets = this.setsForPair(this.state.tableId, this.state.chairId);
        const hideArea = sets.length <= 1 && Number(sets[0]?.chairCount) === 0;
        if (area) area.hidden = hideArea;

        container.style.setProperty('--pairing-set-columns', String(Math.max(sets.length, 1)));
        container.innerHTML = sets.map((set, index) => {
          const active = set.chairCount === Number(this.state.chairCount);
          const available = this.setAvailable(set.chairCount);
          const label = this.setLabelFor(set.chairCount, set.setLabel);
          return `
            <button
              type="button"
              class="product-pairing__set-button${active ? ' is-active' : ''}"
              data-set-index="${index}"
              data-chair-count="${set.chairCount}"
              aria-pressed="${active ? 'true' : 'false'}"
              ${available ? '' : 'disabled'}
            ><span>${this.escapeHtml(label)}</span></button>
          `;
        }).join('');
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      escapeAttr(value) {
        return this.escapeHtml(value).replace(/'/g, '&#39;');
      }

      initializeSliderLoops(types = ['table', 'chair']) {
        for (const type of types) {
          const slider = this.querySelector(`[data-product-slider="${type}"]`);
          const track = slider?.querySelector('[data-product-track]');
          if (!slider || !track) continue;
          const slides = [...track.children].filter((child) => child.hasAttribute('data-product-slide'));
          const total = slides.length;
          slider.querySelectorAll('[data-pairing-arrow]').forEach((arrow) => {
            arrow.hidden = total < 2;
          });
          const dots = slider.querySelector('[data-pairing-dots]');
          if (dots) dots.hidden = total < 2;

          if (total < 2) {
            slider.dataset.loopReady = 'false';
            track.classList.add('is-resetting');
            track.style.transform = 'translateX(0)';
            track.getBoundingClientRect();
            track.classList.remove('is-resetting');
            continue;
          }

          if (slider.dataset.loopReady !== 'true') {
            track.querySelectorAll('.product-pairing__slide--clone').forEach((clone) => clone.remove());
            track.prepend(this.createLoopClone(slides[total - 1]));
            track.append(this.createLoopClone(slides[0]));
            slider.dataset.loopReady = 'true';
          }
          this.normalizeSlider(type, track);
        }
      }

      cancelSliderTransition(type, transition) {
        if (this.sliderTransitions.get(type) !== transition) return false;
        if (transition.onEnd) transition.track.removeEventListener('transitionend', transition.onEnd);
        if (transition.timer != null) window.clearTimeout(transition.timer);
        if (transition.startFrame != null) window.cancelAnimationFrame(transition.startFrame);
        if (transition.resetFrame != null) window.cancelAnimationFrame(transition.resetFrame);
        this.sliderTransitions.delete(type);
        return true;
      }

      normalizeSlider(type, track, releaseLock = true) {
        const active = this.sliderTransitions.get(type);
        if (active) this.cancelSliderTransition(type, active);
        const total = this.sliderTotal(type);
        const stableIndex = total > 1 ? this.currentIndex(type) + 1 : 0;
        track.classList.add('is-resetting');
        track.style.transform = `translateX(-${stableIndex * 100}%)`;
        track.getBoundingClientRect();
        if (!releaseLock) return;
        const transition = {
          track, onEnd: null, timer: null, startFrame: null, resetFrame: null,
        };
        this.sliderTransitions.set(type, transition);
        transition.resetFrame = window.requestAnimationFrame(() => {
          if (this.sliderTransitions.get(type) !== transition) return;
          transition.resetFrame = null;
          track.classList.remove('is-resetting');
          this.sliderTransitions.delete(type);
        });
      }

      handleClick(event) {
        const productLink = event.target.closest('[data-product-link]');
        if (productLink) {
          if (this.suppressProductLinkClick) {
            event.preventDefault();
            this.suppressProductLinkClick = false;
          }
          return;
        }
        this.suppressProductLinkClick = false;

        const coupon = event.target.closest('[data-pairing-coupon]');
        if (coupon) {
          this.couponSelected = Boolean(coupon.checked);
          this.update();
          return;
        }
        const arrow = event.target.closest('[data-pairing-arrow]');
        if (arrow) {
          const type = arrow.dataset.productType;
          const direction = Number(arrow.dataset.direction);
          this.move(type, direction);
          return;
        }
        const dot = event.target.closest('[data-pairing-dot]');
        if (dot) {
          this.select(dot.dataset.productType, Number(dot.dataset.index));
          return;
        }
        const setButton = event.target.closest('[data-set-index]');
        if (setButton && !setButton.disabled) {
          this.select('set', Number(setButton.dataset.setIndex));
          return;
        }
        const addButton = event.target.closest('[data-pairing-add]');
        if (addButton) this.addToCart(addButton);
      }

      handleKeydown(event) {
        const dot = event.target.closest('[data-pairing-dot]');
        if (!dot || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const type = dot.dataset.productType;
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        if (!this.move(type, direction)) return;
        this.querySelector(
          `[data-pairing-dot][data-product-type="${type}"][data-index="${this.currentIndex(type)}"]`,
        )?.focus();
      }

      handlePointerDown(event) {
        const viewport = event.target.closest('.product-pairing__viewport');
        const slider = viewport?.closest('[data-product-slider]');
        if (!slider || event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragStarts.set(event.pointerId, { x: event.clientX, y: event.clientY, slider });
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
      }

      handlePointerUp(event) {
        const start = this.dragStarts.get(event.pointerId);
        if (!start) return;
        this.dragStarts.delete(event.pointerId);
        start.slider.querySelector('.product-pairing__viewport')?.classList.remove('is-dragging');
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        if (Math.abs(deltaX) >= 10 || Math.abs(deltaY) >= 10) {
          this.suppressProductLinkClick = true;
        }
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        const type = start.slider.dataset.productSlider;
        this.move(type, deltaX < 0 ? 1 : -1);
      }

      handlePointerCancel(event) {
        const start = this.dragStarts.get(event.pointerId);
        start?.slider.querySelector('.product-pairing__viewport')?.classList.remove('is-dragging');
        this.dragStarts.delete(event.pointerId);
      }

      move(type, direction) {
        if (!['table', 'chair'].includes(type) || ![-1, 1].includes(direction)) return false;
        if (this.sliderTransitions.has(type)) return false;
        const total = this.sliderTotal(type);
        if (total < 2) return false;
        return this.select(type, cyclicIndex(this.currentIndex(type), direction, total), direction);
      }

      transitionSlider(type, track, { fromIndex, toIndex, direction }) {
        const total = this.sliderTotal(type);
        const stableIndex = total > 1 ? toIndex + 1 : 0;
        const visualIndex = total > 1 ? loopTrackIndex(fromIndex, toIndex, direction, total) : 0;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        if (reduceMotion || total < 2) {
          track.classList.add('is-resetting');
          track.style.transform = `translateX(-${stableIndex * 100}%)`;
          track.getBoundingClientRect();
          track.classList.remove('is-resetting');
          return;
        }

        const transition = {
          track, onEnd: null, timer: null, startFrame: null, resetFrame: null, started: false,
        };
        const finish = () => {
          if (this.sliderTransitions.get(type) !== transition) return;
          if (transition.onEnd) track.removeEventListener('transitionend', transition.onEnd);
          if (transition.timer != null) {
            window.clearTimeout(transition.timer);
            transition.timer = null;
          }
          if (visualIndex !== stableIndex) {
            track.classList.add('is-resetting');
            track.style.transform = `translateX(-${stableIndex * 100}%)`;
            track.getBoundingClientRect();
            transition.resetFrame = window.requestAnimationFrame(() => {
              if (this.sliderTransitions.get(type) !== transition) return;
              transition.resetFrame = null;
              track.classList.remove('is-resetting');
              this.sliderTransitions.delete(type);
            });
          } else {
            this.sliderTransitions.delete(type);
          }
        };
        const onEnd = (event) => {
          if (transition.started && event.target === track && event.propertyName === 'transform') finish();
        };
        transition.onEnd = onEnd;
        this.sliderTransitions.set(type, transition);
        track.addEventListener('transitionend', onEnd);
        track.classList.remove('is-resetting');
        transition.startFrame = window.requestAnimationFrame(() => {
          if (this.sliderTransitions.get(type) !== transition) return;
          transition.startFrame = null;
          transition.started = true;
          track.style.transform = `translateX(-${visualIndex * 100}%)`;
          transition.timer = window.setTimeout(finish, 400);
        });
      }

      select(type, index, direction = 0) {
        if (!['table', 'chair', 'set'].includes(type) || !Number.isInteger(index) || index < 0) return false;
        if (type !== 'set' && this.sliderTransitions.has(type)) return false;

        const fromIndex = this.currentIndex(type);
        if (type === 'table') {
          if (index >= this.tables.length) return false;
          if (this.tables[index].tableId === this.state.tableId) return false;
          this.pendingSliderTransition = { type, fromIndex, toIndex: index, direction };
          this.lastSelectionType = type;
          this.state.tableId = this.tables[index].tableId;
          const chairs = this.chairsForTable(this.state.tableId);
          this.state.chairId = chairs[0]?.chairId ?? null;
          const sets = this.setsForPair(this.state.tableId, this.state.chairId);
          const available = sets.find((set) => this.setAvailable(set.chairCount)) || sets[0];
          this.state.chairCount = available?.chairCount ?? 0;
          this.rebuildChairSlider({ resetPosition: true });
          this.rebuildSetButtons();
          this.initializeSliderLoops(['chair']);
          this.update();
          this.pendingSliderTransition = null;
          return true;
        }

        if (type === 'chair') {
          const chairs = this.chairsForTable(this.state.tableId);
          if (index >= chairs.length) return false;
          if (chairs[index].chairId === this.state.chairId) return false;
          this.pendingSliderTransition = { type, fromIndex, toIndex: index, direction };
          this.lastSelectionType = type;
          this.state.chairId = chairs[index].chairId;
          const sets = this.setsForPair(this.state.tableId, this.state.chairId);
          if (!sets.some((set) => set.chairCount === Number(this.state.chairCount) && this.setAvailable(set.chairCount))) {
            const available = sets.find((set) => this.setAvailable(set.chairCount)) || sets[0];
            this.state.chairCount = available?.chairCount ?? 0;
          }
          this.rebuildSetButtons();
          this.update();
          this.pendingSliderTransition = null;
          return true;
        }

        const sets = this.setsForPair(this.state.tableId, this.state.chairId);
        if (index >= sets.length) return false;
        if (sets[index].chairCount === Number(this.state.chairCount)) return false;
        this.lastSelectionType = type;
        this.state.chairCount = sets[index].chairCount;
        this.update();
        return true;
      }

      update() {
        if (!this.state) return;
        this.ensureValidState();
        const selectionValid = this.resolveSelection();
        const entry = this.currentEntry();

        for (const type of ['table', 'chair']) {
          const slider = this.querySelector(`[data-product-slider="${type}"]`);
          const track = slider?.querySelector('[data-product-track]');
          const transition = this.pendingSliderTransition?.type === type
            ? this.pendingSliderTransition
            : null;
          const total = this.sliderTotal(type);
          const index = this.currentIndex(type);
          if (track && !this.sliderTransitions.has(type)) {
            if (transition) {
              this.transitionSlider(type, track, transition);
            } else {
              const offset = slider?.dataset.loopReady === 'true' && total > 1 ? index + 1 : index;
              track.style.transform = `translateX(-${(total ? offset : 0) * 100}%)`;
            }
          }
          slider?.querySelectorAll('[data-product-slide]').forEach((slide) => {
            const active = Number(slide.dataset.index) === index;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', String(!active));
          });
          slider?.querySelectorAll('[data-pairing-dot]').forEach((dot) => {
            const active = Number(dot.dataset.index) === index;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-pressed', String(active));
            dot.tabIndex = active ? 0 : -1;
          });
        }

        const details = this.querySelector('[data-pairing-details]');
        const detailsUrl = variantDetailsUrl(this.tableProduct, this.tableVariant);
        if (details) {
          if (detailsUrl) {
            details.setAttribute('href', detailsUrl);
            details.removeAttribute('aria-disabled');
            details.removeAttribute('tabindex');
          } else {
            details.removeAttribute('href');
            details.setAttribute('aria-disabled', 'true');
            details.setAttribute('tabindex', '-1');
          }
        }

        const count = displayChairCount(this.state.chairCount);
        const quantity = (this.dataset.chairQuantityTemplate || '__COUNT__').replace('__COUNT__', String(count));
        this.querySelectorAll('[data-chair-quantity]').forEach((element) => { element.textContent = quantity; });
        this.querySelectorAll('[data-chair-thumbnail]').forEach((element, index) => { element.hidden = index >= count; });
        const chairEntry = this.chairsForTable(this.state.tableId)
          .find((item) => item.chairId === this.state.chairId);
        const chairImage = displayImage(chairEntry?.chair, chairEntry?.chairVariant)?.src;
        if (chairImage) {
          this.querySelectorAll('[data-chair-thumbnail-image]').forEach((image) => {
            image.src = chairImage;
            image.removeAttribute('srcset');
          });
        }

        this.querySelectorAll('[data-set-index]').forEach((button) => {
          const chairCount = Number(button.dataset.chairCount);
          const unavailable = !this.setAvailable(chairCount);
          button.disabled = unavailable;
          button.classList.toggle('is-active', chairCount === Number(this.state.chairCount));
          button.setAttribute('aria-pressed', String(chairCount === Number(this.state.chairCount)));
        });

        const displayPrice = this.couponSelected ? this.prices?.promotional : this.prices?.original;
        this.setMoney('[data-pairing-price]', displayPrice);
        const compare = this.querySelector('[data-pairing-compare-price]');
        const save = this.querySelector('[data-pairing-save]');
        const priceMeta = this.querySelector('[data-pairing-price-meta]');
        let compareCents = null;
        if (this.couponSelected && this.prices?.original != null) {
          // Coupon on: strike through pre-coupon set price (or product compare-at if higher).
          compareCents = this.prices.compareAt && this.prices.compareAt > this.prices.original
            ? this.prices.compareAt
            : this.prices.original;
        } else if (this.prices?.showCompare) {
          compareCents = this.prices.compareAt;
        }
        if (compare) {
          compare.hidden = compareCents == null;
          if (compareCents != null) compare.innerHTML = this.formatMoney(compareCents);
        }
        const saveCents = compareCents != null && displayPrice != null && compareCents > displayPrice
          ? compareCents - displayPrice
          : null;
        if (save) {
          save.hidden = saveCents == null;
          if (saveCents != null) {
            const template = this.dataset.saveTemplate || 'Save __AMOUNT__';
            save.innerHTML = template.replace('__AMOUNT__', this.formatMoney(saveCents));
          } else {
            save.textContent = '';
          }
        }
        if (priceMeta) {
          priceMeta.hidden = compareCents == null && saveCents == null;
        }

        const tableUrl = variantDetailsUrl(this.tableProduct, this.tableVariant);
        this.querySelectorAll('[data-product-slider="table"] [data-product-link]').forEach((link) => {
          const slide = link.closest('[data-product-slide]');
          if (!slide || Number(slide.dataset.productId) !== Number(this.state.tableId)) return;
          if (tableUrl) link.setAttribute('href', tableUrl);
        });
        const chairUrl = variantDetailsUrl(this.chairProduct, this.chairVariant);
        this.querySelectorAll('[data-product-slider="chair"] [data-product-link]').forEach((link) => {
          const slide = link.closest('[data-product-slide]');
          if (!slide || Number(slide.dataset.productId) !== Number(this.state.chairId)) return;
          if (chairUrl) link.setAttribute('href', chairUrl);
        });

        const add = this.querySelector('[data-pairing-add]');
        this.currentCanAdd = Boolean(selectionValid && this.setPurchasable(this.state.chairCount));
        if (add && !add.classList.contains('btn--loading')) {
          add.disabled = !this.currentCanAdd;
          add.setAttribute('aria-disabled', String(!this.currentCanAdd));
        }

        if (!selectionValid) this.showError('');

        this.updateScene(entry?.id);
        const status = this.querySelector('[data-pairing-status]');
        if (status && this.lastSelectionType) {
          const selectedControl = this.lastSelectionType === 'set'
            ? this.querySelector(`[data-set-index="${this.currentIndex('set')}"]`)
            : this.querySelector(`[data-product-slider="${this.lastSelectionType}"]`);
          const total = this.lastSelectionType === 'set' ? '' : this.sliderTotal(this.lastSelectionType);
          const position = this.lastSelectionType === 'set' ? '' : (this.dataset.slidePosition || '__POSITION__ / __TOTAL__')
            .replace('__POSITION__', String(this.currentIndex(this.lastSelectionType) + 1))
            .replace('__TOTAL__', String(total));
          const selectedText = this.lastSelectionType === 'set'
            ? selectedControl?.textContent
            : selectedControl?.querySelector('.is-active')?.textContent;
          const unavailableSets = [...this.querySelectorAll('[data-set-index]:disabled')].map((button) => button.textContent.trim());
          status.textContent = [selectedControl?.getAttribute('aria-label'), position, selectedText?.trim(), ...unavailableSets]
            .filter(Boolean)
            .join('. ');
        }
      }

      resolvePairLines(entry) {
        if (!entry) {
          return {
            tableProduct: null,
            tableVariant: null,
            chairProduct: null,
            chairVariant: null,
            chairQuantity: 0,
            valid: false,
          };
        }
        const tableResult = resolveLineVariant(entry.table, entry.tableVariant);
        const chairResult = resolveLineVariant(entry.chair, entry.chairVariant);
        const chairQuantity = displayChairCount(entry.chairCount);
        return {
          tableProduct: entry.table || null,
          tableVariant: tableResult.variant,
          chairProduct: entry.chair || null,
          chairVariant: chairResult.variant,
          chairQuantity,
          valid: tableResult.status === 'matched' && chairResult.status === 'matched',
        };
      }

      resolveSelection() {
        const entry = this.currentEntry();
        const lines = this.resolvePairLines(entry);
        this.tableProduct = lines.tableProduct;
        this.tableVariant = lines.tableVariant;
        this.chairProduct = lines.chairProduct;
        this.chairVariant = lines.chairVariant;
        this.chairQuantity = lines.chairQuantity;
        this.configurationValid = Boolean(this.baseValid && entry && lines.valid);
        this.prices = pairPrices(
          this.tableVariant,
          this.chairVariant,
          this.state.chairCount,
          this.discount?.percentage,
        );
        return Boolean(this.configurationValid && this.prices && String(this.discount?.code || '').trim());
      }

      setAvailable(chairCount) {
        const entry = this.entries.find((item) => (
          item.tableId === this.state.tableId
          && item.chairId === this.state.chairId
          && Number(item.chairCount) === Number(chairCount)
        ));
        // Scene/set switching only needs a configured entry with table + chair products.
        return Boolean(entry?.table && entry?.chair);
      }

      setPurchasable(chairCount) {
        const entry = this.entries.find((item) => (
          item.tableId === this.state.tableId
          && item.chairId === this.state.chairId
          && Number(item.chairCount) === Number(chairCount)
        ));
        const lines = this.resolvePairLines(entry);
        return Boolean(
          lines.valid
          && canFulfill(lines.tableVariant, 1)
          && canFulfill(lines.chairVariant, lines.chairQuantity),
        );
      }

      updateScene(key) {
        if (!key) return;
        this.pendingSceneKey = key;
        const next = this.querySelector(`[data-scene-key="${key}"]`);
        if (!next || next.classList.contains('is-active')) return;
        const activate = () => {
          if (this.pendingSceneKey !== key) return;
          this.querySelectorAll('[data-scene-key]').forEach((layer) => {
            const active = layer === next;
            layer.classList.toggle('is-active', active);
            layer.setAttribute('aria-hidden', String(!active));
          });
        };
        const image = next.querySelector('img');
        if (!image) return;
        if (image.complete) {
          if (image.naturalWidth > 0) activate();
        } else {
          image.addEventListener('load', activate, { once: true });
          image.addEventListener('error', () => {}, { once: true });
        }
      }

      setMoney(selector, cents) {
        const element = this.querySelector(selector);
        if (element) element.innerHTML = cents == null ? '' : this.formatMoney(cents);
      }

      formatMoney(cents) {
        if (window.FoxTheme?.Currency?.formatMoney) return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'USD' }).format((cents || 0) / 100);
      }

      showError(message) {
        const error = this.querySelector('[data-pairing-error]');
        if (error) { error.textContent = message || ''; error.hidden = !message; }
      }

      existingDiscountCodes() {
        return [...document.querySelectorAll('.cart__discounts .discount')]
          .map((element) => element.dataset.discountCode)
          .filter(Boolean);
      }

      cartSections() {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }),
        );
        return sections;
      }

      showDiscountError(message) {
        const discountForm = this.cartDrawer?.querySelector('form[is="cart-discount"]') ||
          document.querySelector('form[is="cart-discount"]');
        if (typeof discountForm?.displayFormErrors === 'function') {
          discountForm.displayFormErrors(message);
        } else {
          this.showError(message);
        }
      }

      async addToCart(button) {
        if (!this.currentCanAdd || button.classList.contains('btn--loading')) return;
        if (!this.tableVariant?.id || !this.chairVariant?.id) return;
        const items = [
          { id: this.tableVariant.id, quantity: 1 },
          { id: this.chairVariant.id, quantity: displayChairCount(this.state.chairCount) },
        ];
        const sections = this.cartSections();
        let addedResult = null;
        let finalSections = null;
        let itemsAdded = false;

        button.classList.add('btn--loading');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.showError('');

        try {
          const addResponse = await fetch(FoxTheme.routes.cart_add_url, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          addedResult = await addResponse.json();
          if (!addResponse.ok || addedResult.status) throw new Error(addedResult.description || this.dataset.cartError);
          itemsAdded = true;
          finalSections = addedResult.sections;

          if (this.couponSelected) {
            const codes = mergeDiscountCodes(this.existingDiscountCodes(), this.discount.code);
            const updateConfig = FoxTheme.utils.fetchConfig('javascript');
            updateConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete updateConfig.headers['Content-Type'];
            const formData = new FormData();
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
            formData.append('discount', codes.join(','));
            updateConfig.body = formData;

            const discountResponse = await fetch(FoxTheme.routes.cart_update_url, updateConfig);
            const discountResult = await discountResponse.json();
            finalSections = discountResult.sections || finalSections;
            if (!discountResponse.ok || !discountIsApplicable(discountResult, this.discount.code)) {
              throw Object.assign(new Error(this.dataset.discountError), { discountFailure: true });
            }
          }

          const cart = await (await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig('json', 'GET'))).json();
          cart.sections = finalSections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: addedResult } }));
          if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType !== 'drawer') {
            window.location.href = FoxTheme.routes.cart_url;
          } else {
            this.cartDrawer?.show(button);
          }
        } catch (error) {
          if (itemsAdded) {
            try {
              const cart = await (await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig('json', 'GET'))).json();
              cart.sections = finalSections;
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
            } catch (_refreshError) {
              // The successful add response remains authoritative if refreshing the cart fails.
            }
            if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType !== 'drawer') {
              window.location.href = FoxTheme.routes.cart_url;
            } else {
              this.cartDrawer?.show(button);
            }
            this.showDiscountError(this.dataset.discountError);
          } else {
            this.showError(error.message || this.dataset.cartError);
          }
        } finally {
          button.classList.remove('btn--loading');
          button.removeAttribute('aria-busy');
          this.update();
        }
      }
    }
  );
}
